import { ArrowRight, Mail, Smartphone } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { SmsFallbackBanner } from '../components/auth/SmsFallbackBanner';
import { OtpInput } from '../components/forms/OtpInput';
import { useCountdownSeconds } from '../hooks/useCountdownSeconds';
import { getOtpErrorMessage } from '../lib/authErrors';
import { normalizeContact } from '../lib/contact';
import { formatUsPhone } from '../lib/phone';
import { toast } from '../lib/toast';
import { cn } from '../lib/utils';
import { useApplicationStore } from '../store/applicationStore';
import { useAuthStore } from '../store/authStore';

import type { OtpType } from '@/types/auth.types';

export function AuthPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [method, setMethod] = useState<OtpType>('email');
  const [contact, setContact] = useState('');
  const [step, setStep] = useState<'contact' | 'otp'>('contact');
  const [otp, setOtp] = useState('');
  const [contactErrorKey, setContactErrorKey] = useState<
    'auth.errors.invalidEmail' | 'auth.errors.invalidPhone' | null
  >(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [otpExpiresAt, setOtpExpiresAt] = useState<number | null>(null);

  const setStoreContact = useAuthStore((state) => state.setContact);
  const sendOtp = useAuthStore((state) => state.sendOtp);
  const verifyOtp = useAuthStore((state) => state.verifyOtp);
  const loginWithDeviceTrust = useAuthStore((state) => state.loginWithDeviceTrust);
  const otpNextAllowedAt = useAuthStore((state) => state.otpNextAllowedAt);
  const otpSent = useAuthStore((state) => state.otpSent);
  const isLoading = useAuthStore((state) => state.isLoading);
  const deviceTrustToken = useAuthStore((state) => state.deviceTrustToken);
  const hasHydrated = useAuthStore((state) => state.hasHydrated);
  const lastErrorCode = useAuthStore((state) => state.lastErrorCode);
  const clearAuthError = useAuthStore((state) => state.clearError);
  const resetOtpFlow = useAuthStore((state) => state.resetOtpFlow);

  const initForApplicant = useApplicationStore((state) => state.initForApplicant);
  const syncGuestDraft = useApplicationStore((state) => state.syncGuestDraft);

  const retryAfterSeconds = useCountdownSeconds(otpNextAllowedAt);
  const otpExpirySeconds = useCountdownSeconds(otpExpiresAt);

  // Boolean OR for disabled state - these conditions should disable when ANY is true
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const isSendDisabled = isLoading || retryAfterSeconds > 0 || (method === 'sms' && !smsConsent);
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const isVerifyDisabled = isLoading || otp.length < 6;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const isResendDisabled = isLoading || retryAfterSeconds > 0;

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const location = useLocation();
  const locationState = location.state as {
    jobPostingId?: string;
    jobPostingTitle?: string;
    jobPostingLocation?: string;
    returnTo?: string;
    returnToSubmit?: boolean;
  } | null;
  const jobPostingId = locationState?.jobPostingId;
  const jobPostingTitle = locationState?.jobPostingTitle;
  const jobPostingLocation = locationState?.jobPostingLocation;
  const returnTo = locationState?.returnTo;
  const returnToSubmit = locationState?.returnToSubmit;
  const wizardState = useMemo(
    () => (jobPostingId ? { jobPostingId, jobPostingTitle, jobPostingLocation } : undefined),
    [jobPostingId, jobPostingTitle, jobPostingLocation],
  );

  // If already authenticated, redirect to returnTo or wizard (it will check for existing applications)
  useEffect(() => {
    if (hasHydrated && isAuthenticated) {
      const destination = returnTo ?? (jobPostingId ? '/wizard' : '/jobs');
      const state = returnTo ? { returnToSubmit } : wizardState;
      void navigate(destination, { replace: true, state });
    }
  }, [hasHydrated, isAuthenticated, navigate, jobPostingId, returnTo, returnToSubmit, wizardState]);

  // Attempt device trust login after store hydration completes
  useEffect(() => {
    if (hasHydrated && !isAuthenticated && deviceTrustToken) {
      void loginWithDeviceTrust().then(async (result) => {
        if (result.ok) {
          // Get fresh applicant from store after login
          const loggedInApplicant = useAuthStore.getState().applicant;
          if (loggedInApplicant) {
            initForApplicant(loggedInApplicant.applicantId);
          }
          await syncGuestDraft();
          const destination = returnTo ?? (jobPostingId ? '/wizard' : '/jobs');
          const state = returnTo ? { returnToSubmit } : wizardState;
          void navigate(destination, { state });
        }
        // If failed, just show the normal login form
      });
    }
  }, [
    hasHydrated,
    isAuthenticated,
    deviceTrustToken,
    loginWithDeviceTrust,
    navigate,
    initForApplicant,
    syncGuestDraft,
    returnTo,
    returnToSubmit,
    jobPostingId,
    wizardState,
  ]);

  useEffect(() => {
    setContactErrorKey(null);
    clearAuthError();
  }, [method, clearAuthError]);

  const handleSwitchToEmail = () => {
    setMethod('email');
    setContact('');
    setSmsConsent(false);
    clearAuthError();
  };

  const handleChangeContact = () => {
    resetOtpFlow();
    setStep('contact');
    setOtp('');
    setOtpError(null);
    setOtpExpiresAt(null);
  };

  const showSmsFallback =
    method === 'sms' &&
    (lastErrorCode === 'SMS_DELIVERY_FAILED' ||
      lastErrorCode === 'OTP_DAILY_CAP_EXCEEDED' ||
      lastErrorCode === 'OTP_MAX_ATTEMPTS_EXCEEDED');

  const handleSendCode = async (event: FormEvent) => {
    event.preventDefault();
    setContactErrorKey(null);

    const raw = contact.trim();
    if (!raw) return;

    if (retryAfterSeconds > 0) {
      toast.error(t('auth.errors.rateLimited', { seconds: retryAfterSeconds }));
      return;
    }

    const normalized = normalizeContact(method, raw);
    if (!normalized.ok) {
      setContactErrorKey(normalized.errorKey);
      return;
    }
    if (method === 'sms') {
      setContact(normalized.value);

      if (!smsConsent) {
        toast.error(t('auth.errors.smsConsentRequired'));
        return;
      }
    }

    setStoreContact(normalized.value, method);
    const result = await sendOtp(method === 'sms' ? { smsConsent: true } : undefined);

    if (!result.ok) {
      if (result.code) {
        toast.error(getOtpErrorMessage(t, { code: result.code, retryAfterSeconds: result.retryAfterSeconds }));
      } else if (result.retryAfterSeconds) {
        toast.error(t('auth.errors.rateLimited', { seconds: result.retryAfterSeconds }));
      } else {
        toast.error(getOtpErrorMessage(t, {}));
      }
      return;
    }

    setOtpExpiresAt(Date.now() + 10 * 60 * 1000);
    setStep('otp');
    toast.success(t('auth.codeSent'));
  };

  const handleOtpChange = (value: string) => {
    setOtpError(null);
    setOtp(value);
  };

  const handleResendCode = async () => {
    if (retryAfterSeconds > 0 || isLoading) return;

    setOtp('');
    setOtpError(null);

    const result = await sendOtp(method === 'sms' ? { smsConsent: true } : undefined);

    if (!result.ok) {
      if (result.code) {
        toast.error(getOtpErrorMessage(t, { code: result.code, retryAfterSeconds: result.retryAfterSeconds }));
      } else if (result.retryAfterSeconds) {
        toast.error(t('auth.errors.rateLimited', { seconds: result.retryAfterSeconds }));
      } else {
        toast.error(getOtpErrorMessage(t, {}));
      }
      return;
    }

    setOtpExpiresAt(Date.now() + 10 * 60 * 1000);
    toast.success(t('auth.codeSent'));
  };

  const handleVerify = async (event: FormEvent) => {
    event.preventDefault();

    const result = await verifyOtp(otp, rememberDevice);

    if (!result.ok) {
      setOtpError(getOtpErrorMessage(t, { code: result.code, retryAfterSeconds: result.retryAfterSeconds }));
      return;
    }

    // Get fresh applicant from store after login and initialize application
    const loggedInApplicant = useAuthStore.getState().applicant;
    if (loggedInApplicant) {
      initForApplicant(loggedInApplicant.applicantId);
    }

    // Sync any guest draft data to the server now that user is authenticated
    await syncGuestDraft();

    localStorage.setItem('applicant_contact', contact);

    // If redirected from wizard guest path, return there
    if (returnTo) {
      void navigate(returnTo, { state: returnToSubmit ? { returnToSubmit } : undefined });
      return;
    }

    void navigate(jobPostingId ? '/wizard' : '/jobs', { state: wizardState });
  };

  return (
    <div className='max-w-md mx-auto mt-8 p-8 bg-white rounded-xl shadow-xl border border-gray-100'>
      <div className='text-center mb-6'>
        <h2 className='text-3xl md:text-4xl font-bold text-brand-gray tracking-tight'>{t('auth.verifyIdentity')}</h2>
        <p className='text-gray-500 text-sm mt-2'>
          {step === 'contact' ? t('auth.sendCodeHint') : `${t('auth.codeSentTo')} ${contact}`}
        </p>
      </div>

      {step === 'contact' ? (
        <form onSubmit={(e) => void handleSendCode(e)}>
          <div className='flex bg-brand-pale p-1 rounded-lg mb-6'>
            <button
              type='button'
              onClick={() => setMethod('email')}
              className={`flex-1 flex items-center justify-center py-2.5 text-base font-medium rounded-md transition ${method === 'email' ? 'bg-white shadow-sm text-primary' : 'text-gray-500 hover:text-gray-700'}`}>
              <Mail size={18} className='mr-2' /> {t('auth.email')}
            </button>
            <button
              type='button'
              onClick={() => setMethod('sms')}
              className={`flex-1 flex items-center justify-center py-2.5 text-base font-medium rounded-md transition ${method === 'sms' ? 'bg-white shadow-sm text-primary' : 'text-gray-500 hover:text-gray-700'}`}>
              <Smartphone size={18} className='mr-2' /> {t('auth.sms')}
            </button>
          </div>

          <div className='mb-6'>
            <label className='block text-sm font-bold text-gray-700 mb-1'>
              {method === 'email' ? t('auth.emailAddress') : t('auth.mobileNumber')}
            </label>
            <div className='relative'>
              {method === 'sms' ? (
                <span
                  aria-hidden='true'
                  className='absolute left-4 top-1/2 -translate-y-1/2 text-brand-gray font-medium pointer-events-none select-none'>
                  +1
                </span>
              ) : null}
              <input
                type={method === 'email' ? 'email' : 'tel'}
                value={contact}
                onChange={(event) => {
                  setContactErrorKey(null);
                  const next = method === 'sms' ? formatUsPhone(event.target.value) : event.target.value;
                  setContact(next);
                }}
                placeholder={method === 'email' ? t('auth.emailPlaceholder') : t('auth.phonePlaceholder')}
                className={`w-full py-3 border rounded-lg focus:ring-2 focus:border-transparent outline-none bg-white text-brand-gray ${
                  method === 'sms' ? 'pl-10 pr-4' : 'px-4'
                } ${contactErrorKey ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary'}`}
                required
                aria-invalid={contactErrorKey ? 'true' : 'false'}
              />
            </div>
            {contactErrorKey ? <p className='mt-2 text-xs text-red-600'>{t(contactErrorKey)}</p> : null}
          </div>

          {method === 'sms' ? (
            <label className='flex items-start gap-2 cursor-pointer mb-4'>
              <input
                type='checkbox'
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className={cn(
                  'mt-0.5 w-4 h-4 rounded border-gray-300 shrink-0',
                  'text-primary focus:ring-primary focus:ring-offset-0',
                )}
              />
              <span className='text-xs text-gray-500 leading-relaxed'>{t('auth.smsConsentNotice')}</span>
            </label>
          ) : null}

          {showSmsFallback ? (
            <SmsFallbackBanner
              variant={
                lastErrorCode === 'OTP_DAILY_CAP_EXCEEDED'
                  ? 'daily_cap'
                  : lastErrorCode === 'OTP_MAX_ATTEMPTS_EXCEEDED'
                    ? 'max_attempts'
                    : 'delivery_failed'
              }
              onSwitchToEmail={handleSwitchToEmail}
              className='mb-4'
            />
          ) : null}

          <button
            type='submit'
            disabled={isSendDisabled}
            className='w-full flex items-center justify-center py-3.5 text-lg bg-primary text-white font-bold rounded-lg hover:bg-primary-dark transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed'>
            {isLoading
              ? t('auth.sending')
              : retryAfterSeconds > 0
                ? t('auth.resendIn', { seconds: retryAfterSeconds })
                : otpSent
                  ? t('auth.resendCode')
                  : t('auth.sendCode')}{' '}
            <ArrowRight size={20} className='ml-2' />
          </button>
        </form>
      ) : (
        <form onSubmit={(e) => void handleVerify(e)}>
          <div className='mb-6'>
            <label className='block text-sm font-bold text-gray-700 mb-3 text-center'>
              {t('auth.verificationCode')}
            </label>
            <OtpInput value={otp} onChange={handleOtpChange} error={otpError ?? undefined} disabled={isLoading} />
            <p className='text-xs text-gray-500 mt-3 text-center'>
              {otpExpirySeconds > 0
                ? t('auth.codeExpiresIn', {
                    time: `${String(Math.floor(otpExpirySeconds / 60)).padStart(2, '0')}:${String(otpExpirySeconds % 60).padStart(2, '0')}`,
                  })
                : t('auth.codeExpired')}
            </p>
          </div>

          <label className='flex items-center gap-2 cursor-pointer mb-6'>
            <input
              type='checkbox'
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              className={cn('w-4 h-4 rounded border-gray-300', 'text-primary focus:ring-primary focus:ring-offset-0')}
            />
            <span className='text-sm text-gray-600'>{t('auth.rememberDevice')}</span>
          </label>

          <button
            type='submit'
            disabled={isVerifyDisabled}
            className='w-full flex items-center justify-center py-3.5 text-lg bg-primary text-white font-bold rounded-lg hover:bg-primary-dark transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed'>
            {isLoading ? t('auth.verifying') : t('auth.verifyContinue')}
          </button>

          <button
            type='button'
            onClick={() => void handleResendCode()}
            disabled={isResendDisabled}
            className='w-full mt-4 text-sm text-primary hover:text-primary-dark disabled:text-gray-400 disabled:cursor-not-allowed'>
            {retryAfterSeconds > 0 ? t('auth.resendIn', { seconds: retryAfterSeconds }) : t('auth.resendCode')}
          </button>

          <button
            type='button'
            onClick={handleChangeContact}
            className='w-full mt-2 text-sm text-gray-500 hover:text-brand-gray'>
            {t('auth.changeContact')}
          </button>
        </form>
      )}
    </div>
  );
}
